import React, { useMemo } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  SlotCapacityAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroAmbient,
  useHeroVerticalPadding,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroPhotoBanner.css";

/**
 * HeroPhotoBanner — the Photo Banner family renderer. Photos
 * dominate the top of the hero in a horizontal strip and the title /
 * body / CTA sit beneath. The layout (strip on top, text below) is
 * identical across every member of the family — what changes is the
 * motion story applied to the strip:
 *
 *   * ``photo_banner``         — per-photo horizontal pan inside a
 *                                fixed-width frame (the canonical
 *                                Photo Banner feel). Driven by
 *                                ``hero-ambient-photo-pan-x`` mounted
 *                                on each ``<img>`` with alternating
 *                                ``--hero-pan-direction`` and
 *                                index-based delay so adjacent frames
 *                                pan against each other (chorus
 *                                effect) instead of in unison.
 *   * ``photo_banner_static``  — no motion. The strip sits still and
 *                                the photos themselves carry the
 *                                visual weight. ``ambientAnimation``
 *                                is ``null`` for this mode so the
 *                                ambient class resolver returns "".
 *   * ``photo_banner_focus``   — the *frame widths* animate: one
 *                                photo at a time grows wider ("in
 *                                focus") while its siblings shrink to
 *                                make room. Driven by
 *                                ``hero-ambient-photo-banner-focus``
 *                                mounted on each *frame container*
 *                                (not the ``<img>``) with a per-frame
 *                                ``--hero-banner-focus-index`` so the
 *                                wide phase staggers around the cycle
 *                                — every photo gets its turn.
 *
 * "Photos at the top" is the visual identity, so the layout always
 * stacks vertically (photos above text), never side-by-side. On
 * mobile the photo strip shrinks but stays on top — same reading
 * order, smaller real estate.
 *
 * Backward compat note: existing user heroes carry
 * ``hero_mode = "photo_banner"`` and continue to render the
 * canonical pan motion unchanged. The new ``_static`` and ``_focus``
 * siblings opt into different motion stories without touching the
 * shared layout, locks, or per-slot overrides (see
 * ``PhotoBannerMode`` and its subclasses in
 * ``hero_modes/definitions.py``).
 */

// Mode-id constants for the family. Keep these in lockstep with the
// backend ``PhotoBannerMode``/``PhotoBannerStaticMode``/
// ``PhotoBannerFocusMode`` ids (and their ``_flush`` siblings) — the
// renderer keys off ``hero_mode`` to decide where to mount the
// ambient class (``<img>`` for the pan, the frame container for the
// width-rotation), since each animation targets a different element.
//
// We canonicalise the id by stripping a trailing ``_flush`` suffix
// before comparing — the flush variants are pure renderer chrome
// flips (square corners / no shadow / zero gap) and inherit the
// motion story of their canonical sibling. Treating
// ``photo_banner_focus_flush`` the same as ``photo_banner_focus``
// for the motion-routing branch keeps that promise honest in one
// place instead of branching every comparison below.
const MODE_PAN = "photo_banner";
const MODE_FOCUS = "photo_banner_focus";
// Visitor-driven sibling of ``MODE_FOCUS`` — every frame sits
// equal-width at rest and the one the visitor hovers / focuses
// stretches wide while its siblings shrink. CSS-only behaviour
// (no JS coordination, no ambient class) so the renderer just
// flags the strip + frames with ``hero-photo-banner-frame--hover-focus``
// and lets ``heroPhotoBanner.css`` own the transition. No flush
// counterpart yet — see the rationale block on
// ``PhotoBannerHoverFocusMode`` in ``definitions.py``; add the
// corresponding canonicalisation branch here once one ships.
const MODE_HOVER_FOCUS = "photo_banner_hover_focus";

function canonicalisePhotoBannerMode(modeId) {
  if (typeof modeId !== "string") return modeId;
  return modeId.endsWith("_flush") ? modeId.slice(0, -"_flush".length) : modeId;
}

// ``bannerVh`` is the photo strip's height as a viewport fraction.
// It used to be 55/50/40/32vh per breakpoint, which dominated the
// hero on typical laptop viewports — the strip would push past
// halfway down the screen, leaving the title/body/CTA crammed
// underneath. Brought in line with the Gallery Strip family
// (45/42/38/30/26vh) so the strip reads as a "banner" rather than
// the entire hero. Carries through to ``photo_banner``,
// ``photo_banner_static``, ``photo_banner_focus``, and every
// ``_flush`` sibling — they all share this renderer + config.
const BP_CONFIG = {
  extra_large: { contentPx: 64, contentPy: 40, bannerVh: "45vh", gap: 12 },
  large:       { contentPx: 56, contentPy: 36, bannerVh: "42vh", gap: 12 },
  medium:      { contentPx: 40, contentPy: 32, bannerVh: "38vh", gap: 10 },
  small:       { contentPx: 24, contentPy: 24, bannerVh: "30vh", gap: 8 },
  extra_small: { contentPx: 16, contentPy: 20, bannerVh: "26vh", gap: 6 },
};

// Cap defensively (backend ``PhotoBannerMode._BANNER_MAX = 4``). Beyond
// 4 frames the strip visually breaks down — each photo is too narrow
// for the pan to register and the row turns into a squashed mosaic.
const MAX_PHOTOS = 4;

// ---------------------------------------------------------------------------
// HeroPhotoBanner main renderer
// ---------------------------------------------------------------------------

const HeroPhotoBanner = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const gallerySlotData = heroSlots?.getSlot("gallery");
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    () => galleryItems.filter((it) => it?.type === "image").slice(0, MAX_PHOTOS),
    [galleryItems],
  );
  const hasImages = images.length > 0;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  // The ambient class string returned here depends on the active
  // mode's ``ambient_animation`` id (declared on the backend
  // ``PhotoBannerMode`` / its subclasses) and is gated by
  // ``prefers-reduced-motion`` inside ``useHeroAmbient`` — when the
  // user opts out of motion this returns "" and the strip sits still
  // regardless of which family member is selected.
  //
  //   ``photo_banner``         → "hero-ambient-photo-pan-x"
  //                             (mounted on each ``<img>``)
  //   ``photo_banner_static``  → ""  (no motion at all)
  //   ``photo_banner_focus``   → "hero-ambient-photo-banner-focus"
  //                             (mounted on each *frame container*)
  //
  // We resolve the class string once and then route it to the right
  // element in the JSX below based on ``modeId``. Centralising the
  // routing here (rather than per-frame) keeps the JSX free of
  // mode-id branching at every render site.
  const ambientClass = useHeroAmbient(heroSlots?.ambientAnimation);
  const modeId = item?.hero_mode;
  const canonicalModeId = canonicalisePhotoBannerMode(modeId);
  const isFocusVariant = canonicalModeId === MODE_FOCUS;
  const isPanVariant = canonicalModeId === MODE_PAN;
  // Hover-driven width-breathing variant — same flex-grow story
  // as ``MODE_FOCUS`` (one frame stretches wide, siblings shrink)
  // but driven by ``:hover`` / ``:focus-within`` instead of a
  // continuous keyframe. We mount a strip-level class that the
  // CSS uses to scope the per-frame ``:hover`` rule (so the
  // hover effect ONLY fires in this mode, never bleeds into the
  // canonical pan / static / auto-focus siblings). The
  // ``ambientClass`` is suppressed for this mode (the strip sits
  // still at rest) — visitors drive the only motion.
  const isHoverFocusVariant = canonicalModeId === MODE_HOVER_FOCUS;
  // ``_flush`` variants share this renderer with their canonical
  // sibling (see dispatch comment in ``HeroAutomatic``). The mode id
  // is the only place we can read which finish the user picked —
  // slot contract / locks / overrides are inherited unchanged. The
  // flush flag flips per-frame chrome (corner radius / shadow /
  // border via the ``--flush`` modifier) AND zeros every spacing
  // axis around the strip: inter-frame gap, leading/trailing strip
  // padding, and the strip's top breathing room. All four go to
  // zero so the four photos read as one continuous strip butting
  // straight against the hero edges.
  const isFlush = typeof modeId === "string" && modeId.endsWith("_flush");
  const frameAmbientClass = isFocusVariant ? ambientClass : "";
  const imgAmbientClass = isPanVariant ? ambientClass : "";

  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  // Vertical break-out for the inner flex column (strip + text).
  // The hero root carries the user's spacing-driven padding (e.g.
  // ``py-12``), so without this the photo strip would sit ~48px
  // below the hero's top edge and the text section ~48px above its
  // bottom edge — a visible band of background framing the content.
  // Photo Banner's identity is "photos own the top of the hero", so
  // we measure the hero root's resolved vertical padding (see
  // ``useHeroVerticalPadding``) and pull the inner column up + down
  // by exactly that amount.
  //
  // Effect at the edges:
  //   * Top    — strip butts directly against the hero top edge.
  //   * Bottom — text section butts directly against the hero bottom
  //              edge (the text still has its own ``contentPy``
  //              padding for breathing room around the heading /
  //              body / CTA, so nothing actually touches the edge,
  //              just the column's box does).
  //
  // We deliberately don't gate this on the ``_flush`` variants —
  // every Photo Banner reads as "photo banner" first, "rounded vs
  // square frames" second, and the band of background above the
  // strip looks unintentional in both finishes.
  //
  // We DO suppress the break-out when ``responsiveHeight: false``:
  // in that mode the inner column is ``absolute inset-0`` and uses
  // CSS positioning rather than flex margins for placement, so
  // negative margins on it would stack with the inset values
  // unpredictably (offset the column in addition to its inset:0
  // anchor). The padding is part of the user's contract in
  // fixed-height mode anyway — they picked a fixed hero height + a
  // spacing value, and the spacing value is supposed to inset the
  // content from the edges.
  const heroPad = useHeroVerticalPadding(heroRootRef);
  const innerBreakoutStyle =
    responsiveHeight && (heroPad.top || heroPad.bottom)
      ? {
          marginTop: heroPad.top ? `-${heroPad.top}px` : 0,
          marginBottom: heroPad.bottom ? `-${heroPad.bottom}px` : 0,
        }
      : null;

  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // Empty state mirrors the other animation-first modes: stack the
  // text centred so the editor still has something readable while
  // the user wires up images.
  if (!hasImages) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty
                gallery slot. Hidden outside the editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`flex flex-col w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={{
          ...(responsiveHeight ? { minHeight: `${baseHeightPx}px` } : null),
          ...(innerBreakoutStyle || {}),
        }}
      >
        {/* Photo strip — always at the top, regardless of breakpoint.
            The strip's height is a viewport unit so it scales smoothly
            with the device, but capped by the row's overall flex
            allocation (text column gets ``flex-1`` below). */}
        <div
          className={`hero-photo-banner-strip flex-shrink-0${
            isHoverFocusVariant ? " hero-photo-banner-strip--hover-focus" : ""
          }`}
          style={{
            height: config.bannerVh,
            gap: isFlush ? 0 : `${config.gap}px`,
            paddingLeft: isFlush ? 0 : config.gap,
            paddingRight: isFlush ? 0 : config.gap,
            // ``paddingTop`` mirrors the same ``config.gap`` we apply
            // on the left/right axes for the non-flush (rounded)
            // variants — a small symmetric breathing pocket above
            // the cards so they don't butt against the hero's top
            // edge (or, when ``innerBreakoutStyle`` cancels the
            // hero root's vertical padding, against the very top of
            // the inner flex column). Without this the rounded
            // chrome reads as "clipped" against the top edge — the
            // shadow + corner radius lose half their effect.
            //
            // We deliberately reuse ``config.gap`` rather than pick
            // a separate value: the rounded photo frames now have
            // visually consistent breathing room on three sides
            // (top, left, right), and the bottom flows directly
            // into the text section's own ``contentPy`` padding.
            //
            // The flush variants still zero this out so the four
            // photos meet the hero's top edge with no gutter — the
            // entire point of the flush finish.
            paddingTop: isFlush ? 0 : config.gap,
          }}
        >
          {images.map((img, idx) => {
            const src = img.cropped_image_url || img.image_url;
            // Alternate pan direction so adjacent frames feel like
            // they're moving against each other (chorus effect)
            // rather than a synchronized parade. Only relevant for
            // the pan variant; harmless on the others (the CSS var
            // simply isn't read when the ambient class isn't
            // mounted).
            const direction = idx % 2 === 0 ? 1 : -1;
            // Frame-level CSS vars — set unconditionally so a future
            // mode swap doesn't leave stale state on the DOM. The
            // focus animation reads ``--hero-banner-focus-index`` to
            // stagger ``animation-delay`` so each frame enters the
            // wide phase at a different point in the cycle (one
            // photo "in focus" at any given time, rotating around
            // the strip). ``--hero-banner-focus-count`` lets the
            // keyframe normalize timing per frame count if we ever
            // want a count-aware curve.
            const frameStyle = {
              "--hero-banner-focus-index": idx,
              "--hero-banner-focus-count": images.length,
            };
            const baseFrameClass = isFlush
              ? "hero-photo-banner-frame hero-photo-banner-frame--flush"
              : "hero-photo-banner-frame";
            // Hover-focus mode adds a per-frame modifier so the
            // CSS can scope its ``:hover`` / ``:focus-within``
            // grow rule to just these frames (the canonical pan /
            // static / auto-focus siblings keep their resting
            // ``flex: 1 1 0`` distribution). Pairs with
            // ``hero-photo-banner-strip--hover-focus`` on the
            // parent — both halves of the selector live in
            // ``heroPhotoBanner.css`` so the rule is easy to find.
            const hoverFocusFrameClass = isHoverFocusVariant
              ? " hero-photo-banner-frame--hover-focus"
              : "";
            const ambientPart = frameAmbientClass ? ` ${frameAmbientClass}` : "";
            const frameClass = `${baseFrameClass}${hoverFocusFrameClass}${ambientPart}`;
            return (
              <div
                key={`photo-banner-${img.id || idx}-${idx}`}
                className={frameClass}
                style={frameStyle}
                tabIndex={isHoverFocusVariant ? 0 : undefined}
              >
                {src ? (
                  (() => {
                    const { className: filterClass, style: filterStyle } =
                      getHeroImageStyle(img);
                    return (
                      <ProgressiveHeroImage
                        src={src}
                        alt={img.alt_text || ""}
                        loading={idx === 0 ? "eager" : "lazy"}
                        draggable={false}
                        className={`${imgAmbientClass} ${filterClass}`.trim()}
                        style={{
                          "--hero-pan-direction": direction,
                          "--hero-pan-index": idx,
                          ...filterStyle,
                        }}
                      />
                    );
                  })()
                ) : (
                  <div className="hero-photo-banner-frame__empty" />
                )}
                <EditableSlotMediaOverlay slotData={img} heroItem={item} />
              </div>
            );
          })}
        </div>
        <SlotCapacityAffordance
          heroSlots={heroSlots}
          heroId={item?.id}
          slotName="gallery"
          currentCount={images.length}
          className="mx-4 mt-3"
        />

        {/* Text section beneath the strip. ``min-w-0`` mirrors Split
            for the same reason: protect against wide intrinsic
            children (e.g. button rows) blowing past the visible
            width. ``flex-1`` lets it absorb the remaining row space
            so the bottom text doesn't crush against the strip. */}
        <div
          ref={contentAnim.ref}
          className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${contentAnim.className}`}
          style={{
            paddingLeft: config.contentPx,
            paddingRight: config.contentPx,
            paddingTop: config.contentPy,
            paddingBottom: config.contentPy,
            ...contentAnim.style,
          }}
        >
          <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroPhotoBanner);
